'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';

interface Particle {
  width: number;
  height: number;
  top: number;
  left: number;
  opacity: number;
  duration: number;
  delay: number;
  isGold: boolean;
}

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    setIsVisible(true);
    const generated: Particle[] = Array.from({ length: 16 }, (_, i) => ({
      width: Math.random() * 5 + 2,
      height: Math.random() * 5 + 2,
      top: Math.random() * 100,
      left: Math.random() * 100,
      opacity: Math.random() * 0.5 + 0.15,
      duration: Math.random() * 6 + 8,
      delay: Math.random() * 5,
      isGold: i % 3 === 0
    }));
    setParticles(generated);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-24"
      style={{
        background: 'linear-gradient(160deg, #0d0818 0%, #130a24 30%, #1a0d30 55%, #130a24 75%, #0d0818 100%)'
      }}>

      {/* Lotus / Mandala Background Pattern */}
      <div className="absolute inset-0 lotus-bg opacity-40 pointer-events-none" />

      {/* Glow Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/4 left-1/4 glow-orb-lavender animate-float-slow"
          style={{ width: '500px', height: '500px', opacity: 0.6 }} />

        <div
          className="absolute bottom-1/4 right-1/4 glow-orb-gold"
          style={{ width: '400px', height: '400px', opacity: 0.45, animation: 'float 14s ease-in-out 2s infinite' }} />

        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 glow-orb-cream"
          style={{ width: '600px', height: '600px', opacity: 0.5 }} />

      </div>

      {/* Sacred Geometry Rings */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: '820px', height: '820px', border: '1px solid rgba(201,168,76,0.12)', animation: 'rotateSlow 60s linear infinite' }} />

        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: '620px', height: '620px', border: '1px solid rgba(155,127,232,0.14)', animation: 'rotateSlow 45s linear infinite reverse' }} />

        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: '440px', height: '440px', border: '1px solid rgba(201,168,76,0.16)', animation: 'rotateSlow 30s linear infinite' }} />

        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: '260px', height: '260px', border: '1.5px solid rgba(155,127,232,0.18)', animation: 'rotateSlow 20s linear infinite reverse' }} />


        {/* Floating particles */}
        {particles.map((p, i) =>
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${p.width}px`,
            height: `${p.height}px`,
            top: `${p.top}%`,
            left: `${p.left}%`,
            opacity: p.opacity,
            background: p.isGold ? '#c9a84c' : '#9b7fe8',
            animation: `drift ${p.duration}s ease-in-out ${p.delay}s infinite`,
            boxShadow: p.isGold ? '0 0 8px rgba(201,168,76,0.7)' : '0 0 8px rgba(155,127,232,0.6)'
          }} />

        )}
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        {/* Banner Image */}
        <div
          className={`flex justify-center mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative w-full max-w-2xl md:max-w-3xl">
            <Image
              src="/assets/images/Untitled_design__4_-1785065616413.png"
              alt="Shreevidha Healing Hub Banner"
              width={900}
              height={300}
              className="object-contain w-full h-auto"
              priority
            />
          </div>
        </div>

        {/* Tagline */}
        <div
          className={`mt-5 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          <div className="flex items-center justify-center gap-3">
            <span className="text-[#c9a84c]/60 text-sm">✦</span>
            <p
              className="font-serif text-xl md:text-2xl font-semibold tracking-widest"
              style={{
                background: 'linear-gradient(135deg, #c9a84c 0%, #e8c96a 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>

              Heal • Balance • Transform
            </p>
            <span className="text-[#c9a84c]/60 text-sm">✦</span>
          </div>
        </div>

        {/* Headline */}
        <div
          className={`mt-6 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          <h3
            className="font-serif text-2xl md:text-4xl font-bold leading-snug"
            style={{ color: '#e8dff5' }}>

            Transform Your Mind, Body &amp; Energy Naturally
          </h3>
        </div>

        {/* Description */}
        <div
          className={`mt-5 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          <p className="text-[#c4b5fd]/75 text-base md:text-lg font-light tracking-wide max-w-3xl mx-auto leading-relaxed">
            Experience profound healing through ancient wisdom and modern techniques. Release emotional blocks, karmic patterns, and subconscious programming to restore balance, peace, and abundance in every area of your life.
          </p>
        </div>

        {/* CTA Buttons */}
        <div
          className={`mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          <a
            href="https://cal.com/shruti-g6cvvv/30min?overlayCalendar=true"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold inline-block">

            ✨ Book 1:1 Consultation
          </a>
          <a href="#courses" className="btn-lavender inline-block">
            🌸 Explore Courses
          </a>
        </div>

        {/* Trust Indicators */}
        <div
          className={`mt-16 flex flex-wrap items-center justify-center gap-8 md:gap-16 transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          {[
          { number: '10+', label: 'Years Experience' },
          { number: '1000+', label: 'Lives Impacted' },
          { number: '15+', label: 'Healing Modalities' }]?.
          map((stat) =>
          <div key={stat?.label} className="text-center">
              <div
              className="font-display text-4xl md:text-5xl font-semibold"
              style={{
                background: 'linear-gradient(135deg, #c9a84c 0%, #e8c96a 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>

                {stat?.number}
              </div>
              <div className="text-[#c4b5fd]/55 text-xs tracking-widest uppercase mt-1">{stat?.label}</div>
            </div>
          )}
        </div>
      </div>
    </section>);

}